<script setup lang="ts">
import { ref, computed, nextTick, onMounted, onBeforeUnmount, useId, useSlots, useTemplateRef, watch } from 'vue'

defineOptions({ inheritAttrs: false })
import { motion, AnimatePresence } from 'motion-v'
import { computePosition, offset, flip, shift, size as floatingSize, type Placement } from '@floating-ui/dom'
import MaterialSymbolsCloseRounded from '~icons/material-symbols/close-rounded'
import BButton from '@/components/common/bButton.vue'
import { bgColors, size as sizeConst } from '@/services/application/constantes'
import type { TColors, TLoadingStatus } from '@/services/application/interfaces'
import type { StrictTailwindRoundedClass } from '@/services/application'
import { useI18n } from 'vue-i18n'

export type ExpandablePillItem = {
  _id: string
  icon?: object
  title: string
  subtitle?: string
  tooltip?: string
  color?: 'error' | 'success' | 'warning' | 'primary' | 'base'
  panelId?: string
  onClick?: () => void
  disabled?: boolean
}

const props = withDefaults(defineProps<{
  label: string
  items: ExpandablePillItem[]
  badgeBg?: string
  badgeText?: string
  cardWidth?: number
  maxHeight?: number
  placement?: Placement
  // BButton props passthrough
  bg?: keyof typeof bgColors
  size?: keyof typeof sizeConst
  haveError?: boolean
  isLoading?: boolean
  disabled?: boolean
  loadingStatus?: TLoadingStatus
  dot?: { content?: string, bg?: TColors } | boolean
  icon?: boolean
  rounded?: StrictTailwindRoundedClass
  bgHover?: keyof typeof bgColors
  buttonContainerClass?: string
  buttonClass?: string
  disabledTooltip?: string
}>(), {
  badgeBg: 'bg-gray-900 dark:bg-gray-100',
  badgeText: 'text-white dark:text-gray-900',
  cardWidth: 320,
  maxHeight: 400,
  placement: 'bottom-start',
  bg: 'base',
  size: 'small',
  rounded: 'rounded-lg',
  buttonContainerClass: '',
  buttonClass: '',
  disabledTooltip: undefined,
})

const emit = defineEmits<{
  itemClick: [item: ExpandablePillItem]
}>()

const { t } = useI18n()
const slots = useSlots()
const panelId = useId()
const titleId = useId()
const closeButtonRef = useTemplateRef<HTMLButtonElement>('close-button')
const hasContentSlot = computed(() => !!slots.content)

// Map Tailwind rounded class to CSS border-radius value
const borderRadiusMap: Record<string, string> = {
  'rounded-none': '0px',
  'rounded-xs': '2px',
  'rounded-sm': '4px',
  'rounded-md': '6px',
  'rounded-lg': '8px',
  'rounded-xl': '12px',
  'rounded-2xl': '16px',
  'rounded-3xl': '24px',
  'rounded-full': '9999px',
}
const cssRadius = computed(() => borderRadiusMap[props.rounded] || '8px')

const isExpanded = ref(false)
const cardMounted = ref(false)
const showContent = ref(false)
const isAnimating = ref(false)
const naturalHeightValue = ref(0)
const activePanel = ref<string | null>(null)
const viewportPadding = 24

// Refs
const reference = ref<HTMLElement | null>(null)
const cardRef = useTemplateRef<HTMLElement>('expandable-pill-card')
const contentRef = useTemplateRef<HTMLElement>('expandable-pill-content')

// Animation rects: "from" is the button, "to" is the computed card destination
const fromRect = ref({ top: 0, left: 0, width: 0, height: 0 })
const toRect = ref({ top: 0, left: 0, width: 0, height: 0 })

let contentResizeObserver: ResizeObserver | null = null

const getFallbackPlacement = () => (
  props.placement.startsWith('bottom')
    ? props.placement.replace('bottom', 'top')
    : props.placement.replace('top', 'bottom')
) as Placement

const createMeasurementElement = (width: number, height: number) => {
  const tempEl = document.createElement('div')
  tempEl.style.cssText = `position:fixed;top:0;left:0;width:${width}px;height:${height}px;visibility:hidden;pointer-events:none;box-sizing:border-box;`
  document.body.appendChild(tempEl)
  return tempEl
}

const computeTargetRect = async (targetHeight: number, targetWidth: number) => {
  const btnEl = reference.value
  if (!btnEl) return null

  const btnRect = btnEl.getBoundingClientRect()
  const tempEl = createMeasurementElement(targetWidth, targetHeight)
  const fallback = getFallbackPlacement()
  let availableHeight = props.maxHeight

  const { x, y } = await computePosition(btnEl, tempEl, {
    strategy: 'fixed',
    placement: props.placement,
    middleware: [
      offset(-btnRect.height),
      flip({ fallbackPlacements: [fallback] }),
      shift({ padding: viewportPadding / 2 }),
      floatingSize({
        padding: viewportPadding / 2,
        apply({ availableHeight: ah }) {
          availableHeight = Math.min(props.maxHeight, ah)
        },
      }),
    ],
  })

  document.body.removeChild(tempEl)

  const clampedHeight = Math.min(targetHeight, availableHeight)
  const clampedY = Math.min(y, window.innerHeight - clampedHeight - viewportPadding / 2)

  return {
    top: Math.max(viewportPadding / 2, clampedY),
    left: x,
    width: targetWidth,
    height: clampedHeight,
  }
}

const syncCardLayout = async () => {
  if (!isExpanded.value) return

  const contentEl = contentRef.value
  if (!contentEl) return

  const effectiveWidth = Math.min(props.cardWidth, window.innerWidth - viewportPadding)
  const naturalHeight = contentEl.offsetHeight + 1
  const targetRect = await computeTargetRect(naturalHeight, effectiveWidth)

  if (!targetRect || !isExpanded.value) return

  naturalHeightValue.value = naturalHeight
  toRect.value = targetRect
}

const open = async () => {
  const btnEl = reference.value
  if (!btnEl) return
  isAnimating.value = true

  // Clamp card width to viewport
  const effectiveWidth = Math.min(props.cardWidth, window.innerWidth - viewportPadding)

  // Snapshot the button's exact screen position
  const btnRect = btnEl.getBoundingClientRect()
  fromRect.value = {
    top: btnRect.top,
    left: btnRect.left,
    width: btnRect.width,
    height: btnRect.height,
  }

  // Use floating-ui to compute where the card should end up
  const tempEl = document.createElement('div')
  tempEl.style.cssText = `position:fixed;width:${effectiveWidth}px;visibility:hidden;pointer-events:none;display:flex;flex-direction:column;box-sizing:border-box;border:1px solid black;`
  // Mock the structure to measure height (content slot uses maxHeight as initial estimate; ResizeObserver syncs after mount)
  if (hasContentSlot.value) {
    tempEl.style.height = `${props.maxHeight}px`
  }
  else {
    tempEl.innerHTML = `
      <div style="padding: 8px 12px 2px; font-size: 14px; line-height: 20px; font-weight: 600;">${props.label}</div>
      <div style="padding: 0 8px 6px; display: flex; flex-direction: column; gap: 4px;">
        ${props.items.map(item => `
          <div style="padding: 6px 10px; display: flex; align-items: center; gap: 10px;">
            <div style="width: 28px; height: 28px; flex-shrink: 0;"></div>
            <div style="display: flex; flex-direction: column;">
              <div style="font-size: 12px; line-height: 1.25; height: 15px;">T</div>
              ${item.subtitle ? '<div style="font-size: 11px; line-height: 1.25; height: 14px;">S</div>' : ''}
            </div>
          </div>
        `).join('')}
      </div>
    `
  }
  document.body.appendChild(tempEl)
  const naturalHeight = tempEl.offsetHeight + 1 // +1px buffer to prevent sub-pixel scrollbars
  naturalHeightValue.value = naturalHeight

  const fallback = getFallbackPlacement()

  let availableHeight = props.maxHeight

  // computePosition needs tempEl in the DOM to measure it
  const { x, y } = await computePosition(btnEl, tempEl, {
    strategy: 'fixed',
    placement: props.placement,
    middleware: [
      offset(-btnRect.height),
      flip({ fallbackPlacements: [fallback] }),
      shift({ padding: viewportPadding / 2 }),
      floatingSize({
        padding: viewportPadding / 2,
        apply({ availableHeight: ah }) {
          availableHeight = Math.min(props.maxHeight, ah)
        },
      }),
    ],
  })

  document.body.removeChild(tempEl)

  const clampedHeight = Math.min(naturalHeight, availableHeight)
  const clampedY = Math.min(y, window.innerHeight - clampedHeight - viewportPadding / 2)

  toRect.value = {
    top: Math.max(viewportPadding / 2, clampedY),
    left: x,
    width: effectiveWidth,
    height: clampedHeight,
  }

  // Phase 1: mount card (label visible immediately)
  isExpanded.value = true
  showContent.value = true // Set to true immediately so items animate in during expansion
  await nextTick()
  cardMounted.value = true
  await nextTick()
  const closeEl = (closeButtonRef.value as any)?.$el ?? closeButtonRef.value
  closeEl?.focus?.()

  // Phase 2: disable overflow hidden after items stagger in
  setTimeout(() => {
    isAnimating.value = false
  }, 450)
}

const close = async () => {
  // Kept before unmounting: the card lingers in the DOM during its exit animation.
  const cardEl = (cardRef.value as any)?.$el ?? cardRef.value
  isAnimating.value = true
  await nextTick()
  showContent.value = false
  cardMounted.value = false
  isExpanded.value = false
  activePanel.value = null
  await nextTick()
  // Back to the trigger only when the focus is still in the card or went down with it: an item's
  // action may have moved it on purpose, e.g. into the search field of the modal it opened.
  const active = document.activeElement
  const isFocusElsewhere = active && active !== document.body && !(cardEl instanceof HTMLElement && cardEl.contains(active))
  if (isFocusElsewhere)
    return
  const triggerButton = reference.value?.querySelector('button, a, [tabindex]') as HTMLElement | null
  triggerButton?.focus()
}

defineExpose({ close })

// Close on outside click
const onClickOutside = (e: MouseEvent) => {
  if (!isExpanded.value) return
  if (activePanel.value) return // Don't close when a panel is active (panels may have teleported elements)
  const target = e.target as Node
  const cardEl = cardRef.value?.$el ?? cardRef.value
  const btn = reference.value
  if (cardEl && !cardEl.contains(target) && btn && !btn.contains(target)) {
    close()
  }
}

// Close on escape
const onKeyDown = (e: KeyboardEvent) => {
  if (!isExpanded.value)
    return

  if (e.key === 'Escape') {
    e.preventDefault()
    e.stopPropagation()
    close()
    return
  }

  // Lightweight focus trap inside the panel
  if (e.key !== 'Tab')
    return

  const cardEl = (cardRef.value as any)?.$el ?? cardRef.value
  if (!(cardEl instanceof HTMLElement))
    return

  const focusable = Array.from(
    cardEl.querySelectorAll<HTMLElement>('button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'),
  ).filter(el => !el.hasAttribute('disabled') && el.offsetParent !== null)

  if (focusable.length === 0)
    return

  const first = focusable[0]
  const last = focusable[focusable.length - 1]
  const active = document.activeElement as HTMLElement | null

  if (e.shiftKey && active === first) {
    e.preventDefault()
    last.focus()
  }
  else if (!e.shiftKey && active === last) {
    e.preventDefault()
    first.focus()
  }
}

const onItemActivate = (item: ExpandablePillItem) => {
  if (item.disabled)
    return
  if (item.panelId) {
    activePanel.value = item.panelId
    return
  }
  if (item.onClick)
    item.onClick()
  emit('itemClick', item)
  close()
}

const onWindowResize = () => {
  if (!isExpanded.value) return
  void syncCardLayout()
}

onMounted(() => {
  if (typeof ResizeObserver !== 'undefined') {
    contentResizeObserver = new ResizeObserver(() => {
      if (!isExpanded.value) return
      void syncCardLayout()
    })
  }

  document.addEventListener('click', onClickOutside, true)
  document.addEventListener('keydown', onKeyDown)
  window.addEventListener('resize', onWindowResize)
})
onBeforeUnmount(() => {
  contentResizeObserver?.disconnect()
  document.removeEventListener('click', onClickOutside, true)
  document.removeEventListener('keydown', onKeyDown)
  window.removeEventListener('resize', onWindowResize)
})

watch(contentRef, (contentEl, previousContentEl) => {
  if (previousContentEl && contentResizeObserver) {
    contentResizeObserver.unobserve(previousContentEl)
  }

  if (contentEl && contentResizeObserver) {
    contentResizeObserver.observe(contentEl)
  }
})

watch(activePanel, async () => {
  if (!isExpanded.value) return
  await nextTick()
  void syncCardLayout()
}, { flush: 'post' })

// Spring config
const springTransition = {
  type: 'spring' as const,
  stiffness: 400,
  damping: 30,
}

const listContainerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.03,
      delayChildren: 0.01,
    },
  },
}

const listItemVariants = {
  hidden: { opacity: 0, y: 10 },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring' as const,
      stiffness: 450,
      damping: 30,
    },
  },
}

// BButton props
const buttonProps = computed(() => ({
  bg: props.bg,
  size: props.size,
  haveError: props.haveError,
  isLoading: props.isLoading,
  disabled: props.disabled,
  loadingStatus: props.loadingStatus,
  dot: props.dot,
  icon: props.icon,
  rounded: props.rounded,
  bgHover: props.bgHover,
}))
</script>

<template>
  <!-- Trigger button (stays in layout) -->
  <div
    ref="reference"
    class="h-min w-min"
    :class="buttonContainerClass"
    v-tooltip="disabled && disabledTooltip ? disabledTooltip : false"
  >
    <BButton
      v-bind="{ ...buttonProps, ...$attrs }"
      :aria-expanded="isExpanded"
      :aria-controls="panelId"
      :aria-haspopup="true"
      :class="buttonClass"
      @click.stop.prevent="open"
    >
      <slot name="button">
        <span data-expandable-label class="whitespace-nowrap">{{ label }}</span>
      </slot>
    </BButton>
  </div>

  <!-- Morphing card: fixed position, animates top/left/width/height from button rect to card rect -->
  <Teleport to="body">
    <AnimatePresence>
      <motion.div
        v-if="cardMounted"
        :id="panelId"
        ref="expandable-pill-card"
        key="expandable-card"
        role="dialog"
        :aria-modal="true"
        :aria-labelledby="titleId"
        :initial="{
          position: 'fixed',
          top: `${fromRect.top}px`,
          left: `${fromRect.left}px`,
          width: `${fromRect.width}px`,
          height: `${fromRect.height}px`,
          borderRadius: cssRadius,
          opacity: 1,
        }"
        :animate="{
          top: `${toRect.top}px`,
          left: `${toRect.left}px`,
          width: `${toRect.width}px`,
          height: isAnimating ? `${toRect.height}px` : 'auto',
          borderRadius: cssRadius,
          opacity: 1,
        }"
        :exit="{
          top: `${fromRect.top}px`,
          left: `${fromRect.left}px`,
          width: `${fromRect.width}px`,
          height: `${fromRect.height}px`,
          borderRadius: cssRadius,
          opacity: 0,
        }"
        :transition="springTransition"
        class="fixed z-50 bg-white/75 dark:bg-[#141E28]/75 backdrop-blur-xl backdrop-saturate-150 border border-white/20 dark:border-white/10 shadow-lg mini-scroll"
        :style="{ 
          willChange: 'top, left, width, height', 
          maxHeight: `${toRect.height}px`,
          overflowY: (isAnimating || naturalHeightValue <= toRect.height) ? 'hidden' : 'auto',
          overflowX: 'hidden'
        }"
      >
        <div ref="expandable-pill-content">
          <!-- Header: label visible immediately -->
          <div class="flex items-center justify-between px-3 pt-2 pb-0.5">
            <span :id="titleId" class="text-sm font-semibold whitespace-nowrap">{{ label }}</span>

            <motion.button
              ref="close-button"
              type="button"
              :initial="{ scale: 0, opacity: 0 }"
              :animate="{ scale: 1, opacity: 1 }"
              :transition="{ ...springTransition, delay: 0.15 }"
              class="p-1 rounded-full bg-hover cursor-pointer transition-colors"
              :aria-label="t('common.a11y.close')"
              @click.stop="close"
            >
              <MaterialSymbolsCloseRounded class="w-3.5 h-3.5" aria-hidden="true" />
            </motion.button>
          </div>

          <!-- Panel: shown when an item with panelId is clicked -->
          <motion.div
            v-if="showContent && activePanel"
            :initial="{ opacity: 0, x: 10 }"
            :animate="{ opacity: 1, x: 0 }"
            :transition="{ duration: 0.2 }"
            class="px-2 pb-2"
          >
            <slot name="panel" :panel-id="activePanel" :close="close" :back="() => activePanel = null" />
          </motion.div>

          <!-- Custom content: shown immediately when #content slot is provided -->
          <motion.div
            v-else-if="showContent && hasContentSlot"
            :initial="{ opacity: 0, y: 8 }"
            :animate="{ opacity: 1, y: 0 }"
            :transition="{ duration: 0.2 }"
            class="px-2 pb-2"
          >
            <slot name="content" :close="close" />
          </motion.div>

          <!-- Items: appear after container starts expanding -->
          <motion.div
            v-else-if="showContent"
            role="menu"
            :aria-labelledby="titleId"
            :variants="listContainerVariants"
            initial="hidden"
            animate="show"
            class="px-2 pb-1.5 flex flex-col gap-1"
          >
            <motion.div
              v-for="item in items"
              :key="item._id"
              class="w-full"
              :variants="listItemVariants"
              v-tooltip="item.tooltip || false"
            >
              <button
                type="button"
                role="menuitem"
                tabindex="0"
                :data-cy="`item-${item._id}`"
                :disabled="item.disabled"
                class="flex items-center gap-2.5 px-2.5 py-1.5 transition-colors w-full text-left"
                :class="[rounded, item.disabled ? 'opacity-50 cursor-not-allowed bg-fade' : 'bg-hover cursor-pointer']"
                @click="onItemActivate(item)"
              >
                <slot name="item" :item="item">
                  <div
                    v-if="item.icon"
                    class="flex items-center justify-center w-7 h-7 rounded-md shrink-0"
                    aria-hidden="true"
                    :class="[
                      item.color === 'error' ? 'bg-danger-400/20 text-danger-500' :
                      item.color === 'success' ? 'bg-success-400/20 text-success-600' :
                      item.color === 'warning' ? 'bg-warning-400/20 text-warning-600' :
                      item.color === 'primary' ? 'bg-primary-400/20 text-primary-600' :
                      'bg-fade'
                    ]"
                  >
                    <component :is="item.icon" class="w-4 h-4" :class="item.color ? '' : 'opacity-60'" />
                  </div>

                  <div class="flex flex-col min-w-0">
                    <span class="font-medium truncate leading-tight"
                      :class="[
                        item.color === 'error' ? 'text-danger-500' :
                        item.color === 'success' ? 'text-success-600 dark:text-success-500' :
                        item.color === 'warning' ? 'text-warning-600 dark:text-warning-500' :
                        item.color === 'primary' ? 'text-primary-600 dark:text-primary-500' :
                        ''
                      ]"
                    >{{ item.title }}</span>
                    <span v-if="item.subtitle" class="text-[11px] subtitle truncate leading-tight">{{ item.subtitle }}</span>
                  </div>
                </slot>
              </button>
            </motion.div>
          </motion.div>
        </div>
      </motion.div>
    </AnimatePresence>
  </Teleport>
</template>

<style scoped>
/* Scrollbar handled via :style binding to ensure priority over motion-v inline styles */
</style>
